import type { ApiError, ApiFailure, ApiResponseError } from '@/types/api'
import { asRecord, asStrings } from '@/utils/jsonb'

export class AppError extends Error {
  constructor(
    message: string,
    public status: number = 500,
    public detail?: string
  ) {
    super(message)
    this.name = 'AppError'
  }
}

const API_FAILURES: readonly ApiFailure[] = [
  'response',
  'timeout',
  'network',
  'canceled',
]

/**
 * Narrows to what the apiClient boundary rejects with.
 *
 * Keyed on `failure` rather than on `status`: a request that never reached a
 * server carries no status, and a guard that asked for one would have said
 * "not an API error" about exactly the case a caller most needs to read
 * differently (TODO-2053).
 */
export function isApiError(error: unknown): error is ApiError {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { message?: unknown; failure?: unknown; status?: unknown }
  return (
    typeof candidate.message === 'string' &&
    API_FAILURES.includes(candidate.failure as ApiFailure) &&
    (candidate.failure !== 'response' || typeof candidate.status === 'number')
  )
}

/** Narrows to the one arm a server actually answered. */
export function isApiResponseError(error: unknown): error is ApiResponseError {
  return isApiError(error) && error.failure === 'response'
}

/**
 * The HTTP status a server returned, or undefined when none did.
 *
 * The reading every status-branching surface shares, so "no response" cannot
 * be mistaken for a 500 by a caller that forgot to check which arm it holds.
 */
export function responseStatus(error: unknown): number | undefined {
  return isApiResponseError(error) ? error.status : undefined
}

/**
 * Whether a served 409 opens with `reason`.
 *
 * A `ConflictError` puts a code ahead of its sentence ("collection_handle_ambiguous:
 * the handle names both ..."), and the boundary flattens that reason into
 * `detail`. A client branches on the code while a person reads the rest, so the
 * code is matched as a prefix: the sentence is copy and may be reworded.
 */
export function isConflictReason(error: unknown, reason: string): boolean {
  return (
    isApiResponseError(error) &&
    error.status === 409 &&
    (error.detail ?? '').startsWith(reason)
  )
}

/**
 * The auth/availability kind a served refusal carried, when one did.
 *
 * Several surfaces degrade on `forbidden`/`unavailable` rather than showing a
 * failure, and all of them are asking the same question of the same arm.
 */
export function authErrorKind(
  error: unknown
): ApiResponseError['authErrorKind'] {
  return isApiResponseError(error) ? error.authErrorKind : undefined
}

/**
 * A 503 that names when to try again: the database pool shed the request.
 *
 * The boundary classifies every 503 as `unavailable`, but the API sends 503
 * for two reasons. A shed request carries `Retry-After` and the next attempt
 * can succeed; a feature that is switched off answers without it, and no
 * retry succeeds until an operator turns it back on.
 */
export function isShedResponse(error: unknown): boolean {
  return (
    isApiResponseError(error) &&
    error.status === 503 &&
    error.retryAfterSeconds !== undefined
  )
}

/**
 * The surface is withheld from this caller: switched off (a 503 with no
 * `Retry-After`) or not granted (403). A shed is neither, so a surface that
 * degrades on this keeps its last answer through one.
 */
export function isSwitchedOffOrForbidden(error: unknown): boolean {
  const kind = authErrorKind(error)
  return kind === 'forbidden' || (kind === 'unavailable' && !isShedResponse(error))
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return (isApiResponseError(error) ? error.detail : undefined) || error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unexpected error occurred'
}

/**
 * A message for an action's inline failure state. The API boundary determines
 * whether a server answered; callers supply only the action being attempted.
 * A cancellation has no failure state, and a timeout cannot assert that the
 * server abandoned the action.
 */
export function getActionErrorMessage(error: unknown, action: string): string | null {
  if (isApiError(error)) {
    switch (error.failure) {
      case 'canceled':
        return null
      case 'timeout':
        return `The console stopped waiting while trying to ${action} — the request may still be completing.`
      case 'network':
        return `Could not ${action} — check your connection and try again.`
      case 'response':
        if (error.status >= 500) return 'Server error — check application logs.'
        if (error.status === 429) {
          return error.retryAfterSeconds === undefined
            ? 'Rate limited — try again shortly'
            : `Rate limited — try again in ${error.retryAfterSeconds} s`
        }
        return getErrorMessage(error)
    }
  }

  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  return `Could not ${action}.`
}

export function handleApiError(error: unknown): never {
  if (isApiResponseError(error)) {
    throw new AppError(error.message, error.status, error.detail)
  }

  throw new AppError(getErrorMessage(error))
}

interface NormalizedApiErrorDetail {
  /** Human-readable detail string; a 422 detail[] becomes "field — msg; field — msg". */
  detail?: string
  /** Field-keyed messages (leaf `loc` → `msg`) when the source was a 422 detail[]. */
  fieldErrors?: Record<string, string>
  /** An `AppValidationError`'s messages one by one, as `detail` joins them. */
  violations?: string[]
}

/**
 * `AppValidationError`'s own `"<field>: <kind>"` convention (claim
 * write-boundary scan findings — `"statement: secret_pattern"`,
 * `"applicability.key: untrusted_kind"` — dispatch's `"cadence_cron: …"`
 * violations, and any future caller of the same convention). The prefix must
 * look like a field/attribute path (letters, digits, underscore, dot,
 * brackets) so a plain sentence that happens to contain ": " later on (its
 * would-be "field" carries a space) is never mistaken for one.
 */
const FIELD_PREFIXED_ERROR_RE = /^([A-Za-z_][\w.[\]]*): (.+)$/

/**
 * Parse a FastAPI `detail` payload once, at the response-interceptor boundary,
 * into the canonical ApiError fields. FastAPI emits two shapes: a plain string
 * (HTTPException, e.g. 409) and a 422 validation array of `{ loc, msg }`. Both
 * must reach consumers as a ready-to-display `detail`; the array additionally
 * yields a `fieldErrors` map for inline-by-field forms. Parsing here means no
 * consumer re-parses a JSON blob — they read `detail` / `fieldErrors` directly.
 */
export function normalizeApiErrorDetail(rawDetail: unknown): NormalizedApiErrorDetail {
  if (rawDetail == null) return {}
  if (typeof rawDetail === 'string') return { detail: rawDetail }
  if (Array.isArray(rawDetail)) {
    const fieldErrors: Record<string, string> = {}
    const parts: string[] = []
    for (const item of rawDetail) {
      const loc = (item as { loc?: unknown[] })?.loc
      const field =
        Array.isArray(loc) && loc.length ? String(loc[loc.length - 1]) : null
      const rawMsg = (item as { msg?: unknown })?.msg
      const msg = typeof rawMsg === 'string' ? rawMsg : 'invalid'
      if (field) fieldErrors[field] = msg
      // Additive dot-joined body path (PRD-0193 D-14): a nested
      // payload's loc is ["body", "category_overrides", "documentation",
      // "max_files_changed"] — dropping the leading request-part segment
      // gives a form attributing a nested field its own unqualified key
      // (`documentation` vs `security` on the same `max_files_changed`
      // leaf), which the bare last-segment key above cannot express. For a
      // flat (2-element) loc this is byte-identical to `field`, so no
      // existing single-key consumer sees a new entry.
      if (Array.isArray(loc) && loc.length > 1) {
        const bodyPath = loc.slice(1).map(String).join('.')
        if (bodyPath) fieldErrors[bodyPath] = msg
      }
      parts.push(field ? `${field} — ${msg}` : msg)
    }
    return {
      detail: parts.length ? parts.join('; ') : undefined,
      fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined,
    }
  }
  // AppError's dict-shaped detail (Backend/app/exceptions/base.py): unwrap its
  // human-readable members so toasts read as prose instead of a JSON blob.
  if (typeof rawDetail === 'object') {
    const obj = rawDetail as {
      reason?: unknown
      identifier?: unknown
      action?: unknown
      errors?: unknown
    }
    if (typeof obj.reason === 'string') return { detail: obj.reason }
    if (typeof obj.identifier === 'string') return { detail: `Not found: ${obj.identifier}` }
    // AuthorizationError names the refused decision, e.g. that approving a
    // release is an administrator's, taken in the console.
    if (typeof obj.action === 'string') return { detail: obj.action }
    if (Array.isArray(obj.errors)) {
      const messages = asStrings(obj.errors)
      // Additive: split any "<field>: <kind>" entry into fieldErrors too, so a
      // form can surface it inline instead of only in the flattened prose
      // below. Multiple findings for the same field join; a non-prefixed
      // entry (no identifier-shaped text before ": ") flows through the
      // `detail` string unchanged and contributes nothing to fieldErrors.
      const fieldErrors: Record<string, string> = {}
      for (const message of messages) {
        const match = FIELD_PREFIXED_ERROR_RE.exec(message)
        if (!match) continue
        const [, field, kind] = match
        fieldErrors[field] = fieldErrors[field] ? `${fieldErrors[field]}; ${kind}` : kind
      }
      // The list survives beside the joined prose because a message may
      // itself contain "; " (the registry's content-rule findings do), so a
      // surface that shows one per line cannot recover them by splitting.
      return {
        detail: messages.length ? messages.join('; ') : undefined,
        fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined,
        violations: messages.length ? messages : undefined,
      }
    }
    // PreconditionFailedError carries its human-readable version mismatch in
    // the top-level message and an empty detail object. Omitting detail lets
    // getErrorMessage fall back to that clean message instead of displaying {}.
    if (Object.keys(obj).length === 0) return {}
  }
  // Unknown non-empty object shape — stringify as a last resort so nothing is silently dropped.
  return { detail: JSON.stringify(rawDetail) }
}

/** The members of an error body the envelope owns; every other one is `extra`. */
const ENVELOPE_MEMBERS: ReadonlySet<string> = new Set([
  'message',
  'error',
  'domain',
  'detail',
])

/**
 * The members the server merged beside the envelope's own (`AppError.extra`),
 * read once at the response-interceptor boundary like `detail`.
 *
 * A body that is not an object, or carries nothing beyond the envelope, has no
 * extras: undefined rather than an empty record, so a consumer's `?.` reads the
 * same for "nothing sent" and "nothing there".
 */
export function extractApiErrorExtra(
  body: unknown
): Readonly<Record<string, unknown>> | undefined {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return undefined
  }
  const extra = Object.fromEntries(
    Object.entries(body).filter(([key]) => !ENVELOPE_MEMBERS.has(key))
  )
  return Object.keys(extra).length > 0 ? extra : undefined
}

/**
 * The envelope's `detail` when it is an object, read once at the
 * response-interceptor boundary beside `extra`. `normalizeApiErrorDetail` reads
 * one sentence out of such a detail and drops the rest; this keeps the rest for
 * the refusal that answers with structure. A detail that is a string, a
 * validation list or empty has no body: undefined.
 */
export function extractApiErrorDetailBody(
  body: unknown
): Readonly<Record<string, unknown>> | undefined {
  const detail = asRecord(asRecord(body)?.detail)
  return detail !== null && Object.keys(detail).length > 0 ? detail : undefined
}

/**
 * The envelope's `domain`, read once at the response-interceptor boundary like
 * `extra`. A body that is not an object, or whose domain is absent, empty or not
 * a string, names no domain: undefined, so a consumer comparing it never
 * matches on a value the server did not send.
 */
export function extractApiErrorDomain(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null || !('domain' in body)) {
    return undefined
  }
  return typeof body.domain === 'string' && body.domain !== '' ? body.domain : undefined
}

/** A string member of a served refusal's `extra`, or undefined when absent or not a string. */
export function errorExtraString(error: unknown, key: string): string | undefined {
  if (!isApiResponseError(error)) return undefined
  const value = error.extra?.[key]
  return typeof value === 'string' ? value : undefined
}

/**
 * Field-keyed validation messages for a 422, so a form can surface each inline.
 * The boundary already parsed the detail[] into `fieldErrors`; this just reads
 * it. Returns {} for non-422 errors or a 422 without field-level detail.
 */
export function extractFieldErrors(error: unknown): Record<string, string> {
  if (!isApiResponseError(error) || error.status !== 422) return {}
  return error.fieldErrors ?? {}
}
